import { useEffect, useState, useCallback } from 'react'
import { Lock, RefreshCw, AlertTriangle, Users, Key } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

const RISK_TONE: Record<string, string> = {
  high:   'text-rose',
  medium: 'text-amber-light',
  low:    'text-blue-light',
  none:   'text-text-muted',
  hidden: 'text-text-muted',
}

type Tab = 'risky' | 'failures' | 'sps'

export default function IdpRiskyPage() {
  const [tab, setTab] = useState<Tab>('risky')
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [hours, setHours] = useState(24)
  const [staleDays, setStaleDays] = useState(90)

  const fetchData = useCallback(async () => {
    setLoading(true); setData(null)
    try {
      const url = tab === 'risky'    ? '/api/sec/idp/risky-users'
                : tab === 'failures' ? `/api/sec/idp/recent-failures?hours=${hours}`
                : `/api/sec/idp/dormant-sps?stale_days=${staleDays}`
      const r = await api.get(url)
      setData(r.data)
    } finally { setLoading(false) }
  }, [tab, hours, staleDays])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Lock size={24} className="text-rose-light" /> IdP Risky Sign-ins
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Entra ID risky users, recent sign-in failures, dormant service principals. Reads via Microsoft Graph (app permissions).
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {([
          { id: 'risky',    label: 'Risky users',    icon: AlertTriangle },
          { id: 'failures', label: 'Sign-in failures', icon: Users },
          { id: 'sps',      label: 'Service principals', icon: Key },
        ] as const).map(t => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={clsx('px-3 py-2 text-xs font-medium transition-colors flex items-center gap-1.5',
              tab === t.id
                ? 'text-text-primary border-b-2 border-blue -mb-px'
                : 'text-text-muted hover:text-text-secondary')}>
            <t.icon size={11} /> {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {tab === 'failures' && (
            <select value={hours} onChange={e => setHours(parseInt(e.target.value, 10))} className="input py-1 text-[10px]">
              <option value={1}>1h</option><option value={6}>6h</option><option value={24}>24h</option>
              <option value={72}>3d</option><option value={168}>7d</option>
            </select>
          )}
          {tab === 'sps' && (
            <select value={staleDays} onChange={e => setStaleDays(parseInt(e.target.value, 10))} className="input py-1 text-[10px]">
              <option value={30}>30d</option><option value={90}>90d</option><option value={180}>180d</option>
            </select>
          )}
          <button type="button" onClick={fetchData} className="btn-ghost text-[10px]">
            <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {data?.available === false && (
        <div className="card p-4 bg-amber/5 border border-amber/30 text-[11px] text-amber-light">
          <AlertTriangle size={12} className="inline mr-1" />
          {data.reason}. Set <code className="font-mono">ENTRA_TENANT_ID</code> + <code className="font-mono">ENTRA_CLIENT_ID</code> + <code className="font-mono">ENTRA_CLIENT_SECRET</code> in <code className="font-mono">backend/.env</code>.
          Required app permissions:
          <code className="font-mono block mt-1">AuditLog.Read.All · IdentityRiskyUser.Read.All · Application.Read.All</code>
        </div>
      )}

      {tab === 'risky' && data?.users && (
        <div className="card p-3 overflow-x-auto">
          {data.users.length === 0 ? (
            <p className="text-center py-6 text-emerald text-[11px]">No risky users — clean slate.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                  <th className="text-left py-2 px-2">User</th>
                  <th className="text-left py-2 px-2">Risk level</th>
                  <th className="text-left py-2 px-2">State</th>
                  <th className="text-left py-2 px-2">Detail</th>
                  <th className="text-left py-2 px-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u: any) => (
                  <tr key={u.id} className={clsx('border-b border-border/50',
                    u.risk_level === 'high' && 'bg-rose/5')}>
                    <td className="py-1.5 px-2">
                      <div className="text-text-primary font-medium">{u.display_name}</div>
                      <div className="text-[10px] text-text-muted font-mono">{u.user_principal}</div>
                    </td>
                    <td className={clsx('py-1.5 px-2 font-bold uppercase text-[10px]', RISK_TONE[u.risk_level])}>{u.risk_level}</td>
                    <td className="py-1.5 px-2 text-text-secondary">{u.risk_state}</td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">{u.risk_detail || '—'}</td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">{u.last_updated?.slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'failures' && data?.signins && (
        <div className="card p-3 overflow-x-auto">
          {data.signins.length === 0 ? (
            <p className="text-center py-6 text-emerald text-[11px]">No failures or risky sign-ins in window.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                  <th className="text-left py-2 px-2">User</th>
                  <th className="text-left py-2 px-2">App</th>
                  <th className="text-left py-2 px-2">IP / location</th>
                  <th className="text-left py-2 px-2">Risk</th>
                  <th className="text-left py-2 px-2">Reason</th>
                  <th className="text-left py-2 px-2">When</th>
                </tr>
              </thead>
              <tbody>
                {data.signins.map((s: any) => (
                  <tr key={s.id} className="border-b border-border/50">
                    <td className="py-1.5 px-2 font-mono text-[10px]">{s.user}</td>
                    <td className="py-1.5 px-2 text-text-secondary">{s.app}</td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">
                      {s.ip}{s.country && ` · ${s.city || ''} ${s.country}`}
                    </td>
                    <td className={clsx('py-1.5 px-2 text-[10px] font-bold uppercase', RISK_TONE[s.risk_level])}>
                      {s.risk_level !== 'none' ? s.risk_level : '—'}
                    </td>
                    <td className="py-1.5 px-2 text-text-secondary truncate max-w-[260px]" title={s.failure_reason}>{s.failure_reason || '—'}</td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">{s.at?.slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'sps' && data?.service_principals && (
        <div className="card p-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Service Principal</th>
                <th className="text-left py-2 px-2">App ID</th>
                <th className="text-right py-2 px-2">Creds</th>
                <th className="text-left py-2 px-2">Soonest expiry</th>
                <th className="text-left py-2 px-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.service_principals.map((sp: any) => {
                const expSoon = sp.soonest_expiry && new Date(sp.soonest_expiry) < new Date(Date.now() + 30 * 86400_000)
                return (
                  <tr key={sp.id} className={clsx('border-b border-border/50',
                    expSoon && 'bg-amber/5', !sp.enabled && 'opacity-60')}>
                    <td className="py-1.5 px-2">
                      <div className="text-text-primary font-medium">{sp.display_name}</div>
                      {!sp.enabled && <div className="text-[9px] text-rose">disabled</div>}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted truncate max-w-[200px]">{sp.app_id}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-text-primary">{sp.credentials}</td>
                    <td className={clsx('py-1.5 px-2 text-[10px]', expSoon ? 'text-amber-light font-bold' : 'text-text-muted')}>
                      {sp.soonest_expiry?.slice(0, 10) || '—'}
                    </td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">{sp.created?.slice(0, 10) || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
